import { createSelector } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { assignMailboxTagColors } from '../utils/user.util';
import { AccountConnectionStatus, AccountSyncStatus, IInboxSyncState } from '../models/account.models';
import { SmartInboxState, IInboxFilter } from './smart-inbox.reducers';

export const selectSmartInboxState = (state: AppState) => state.smartInbox;

// ─── Connection ──────────────────────────────────────────────────────────────

export const selectConnectionState = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.connectionState
);

// ─── Accounts ────────────────────────────────────────────────────────────────

export const selectAccounts = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.accounts
);

/**
 * Server-backed mailboxes only.
 *
 * The accounts list always carries the client-side "Shared with me" pseudo-account
 * (`id: -1`, injected by `ensureSharedInbox`), which has no mailbox behind it. Any
 * logic that counts or syncs real connections must exclude it.
 */
export const selectRealAccounts = createSelector(
  selectAccounts,
  (accounts) => accounts.filter((account) => account.id !== -1)
);

/**
 * Mailbox-tag tint per connected mailbox id, assigned so that no two mailboxes
 * in the list share one — the tag is there to tell them apart, so a duplicate
 * colour would defeat it.
 */
export const selectMailboxTagColors = createSelector(
  selectRealAccounts,
  (accounts) => assignMailboxTagColors(accounts)
);

// ─── Filter ──────────────────────────────────────────────────────────────────

export const selectFilter = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.filter
);

/** Whether any advanced-filter axis is set; an empty list is no filter. */
export function hasAdvancedFilter(filter: IInboxFilter): boolean {
  return !!(
    filter.categories?.length ||
    filter.counterparties?.length ||
    filter.statuses?.length ||
    filter.assigneeIds?.length ||
    filter.teamIds?.length ||
    filter.from ||
    filter.to ||
    filter.subject ||
    filter.includesWords ||
    filter.excludesWords ||
    filter.dateFrom ||
    filter.dateTo ||
    filter.hasAttachment ||
    filter.isUrgent ||
    filter.customerIds?.length ||
    filter.appliedLabelIds?.length
  );
}

export const selectIsFilterApplied = createSelector(
  selectFilter,
  (filter) => hasAdvancedFilter(filter) || !!filter.searchTerm?.trim()
);

// ─── Conversations ───────────────────────────────────────────────────────────

export const selectConversations = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.conversations
);

export const selectPagination = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.pagination
);

// ─── Active Conversation ─────────────────────────────────────────────────────

export const selectActiveConversation = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.activeConversation
);

export const selectActiveConversationDetails = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.activeConversationDetails
);

export const selectActiveCommentId = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.activeCommentId
);

export const selectActiveMessageId = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.activeMessageId
);

// ─── Selection ───────────────────────────────────────────────────────────────

export const selectSelectedConversationIds = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.selectedConversationIds
);

/** True when bulk action should be "mark as read" (any selected is unread). False when all selected are read. Default true when selection not in list. */
export const selectBulkMarkAsRead = createSelector(
  selectConversations,
  selectSelectedConversationIds,
  (conversations, selectedIds) => {
    if (!selectedIds.length) return true;
    const selected = conversations.filter((c) => selectedIds.includes(c.id));
    if (!selected.length) return true;
    return selected.some((c) => !c.is_read);
  }
);

// ─── Effective Active Accounts (filter.activeAccounts or all accounts) ────

export const selectEffectiveActiveAccounts = createSelector(
  selectAccounts,
  selectFilter,
  (accounts, filter) => {
    const active = filter.activeAccounts ?? [];
    return active.length > 0 ? active : accounts;
  }
);

/**
 * True when the recipient-mailbox tag is shown on every thread, because more than
 * one mailbox is *in view* and the receiving inbox is no longer implicit.
 *
 * Keyed on the accounts actually being listed, not on every connected mailbox: with
 * two connected but one ticked in the sidebar, every visible row would otherwise
 * carry the same tag — the exact noise this feature exists to avoid.
 *
 * `filter.activeAccounts` holds snapshots taken when the sidebar selection was made,
 * so each is re-resolved against the live list before being counted, the same way
 * `selectInboxSyncState` does.
 *
 * Threads reached through the shared inbox always show it regardless — see
 * `buildMailboxTag`.
 */
export const selectMailboxTagAlwaysVisible = createSelector(
  selectAccounts,
  selectEffectiveActiveAccounts,
  (accounts, activeAccounts) => {
    const byId = new Map(accounts.map((account) => [account.id, account]));
    const inView = activeAccounts
      .map((account) => byId.get(account.id) ?? account)
      .filter((account) => account.id !== -1);
    return inView.length >= 2;
  }
);

// ─── Mailbox Sync State ─────────────────────────────────────────────────────

/**
 * Real mailbox sync state for the accounts currently in view, read off the
 * server's `sync_status` — never off a request-in-flight flag.
 *
 * The shared inbox (id `-1`) is a client-side pseudo-account with no mailbox
 * behind it, so it carries no sync state and is skipped.
 */
export const selectInboxSyncState = createSelector(
  selectAccounts,
  selectEffectiveActiveAccounts,
  (accounts, activeAccounts): IInboxSyncState => {
    // `filter.activeAccounts` holds snapshots taken when the sidebar selection
    // was made, so they carry a frozen `sync_status`. Re-resolve each one
    // against the live accounts list before reading any sync field off it.
    const byId = new Map(accounts.map((account) => [account.id, account]));
    const real = activeAccounts
      .map((account) => byId.get(account.id) ?? account)
      .filter((account) => account.id !== -1);

    const syncing = real.filter((a) => a.sync_status === AccountSyncStatus.SYNCING);
    if (syncing.length) {
      return { status: 'syncing', error: null, emails: syncing.map((a) => a.email) };
    }

    // Only a CONNECTED mailbox may raise this banner: a broken connection is not a sync
    // failure actionable from the list, and the sidebar's alert icon owns that story.
    //
    // `=== CONNECTED`, not `!== DISCONNECTED`: an expired-credentials mailbox can sit in
    // `error` or `pending`, and the narrower check let exactly that through — which is how
    // "Sync failed … connection has expired" kept appearing. Also absorbs a stale
    // `sync_status: error` arriving over SSE after a disconnect.
    const errored = real.filter(
      (a) =>
        a.sync_status === AccountSyncStatus.ERROR &&
        a.connection_status === AccountConnectionStatus.CONNECTED
    );
    if (errored.length) {
      const error = errored.map((a) => a.last_sync_error).find((message) => !!message?.trim()) ?? null;
      return { status: 'error', error, emails: errored.map((a) => a.email) };
    }

    return { status: 'idle', error: null, emails: [] };
  }
);

// ─── Active Folder ──────────────────────────────────────────────────────────

export const selectActiveFolder = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.activeFolder
);

// ─── Folders KPIs ──────────────────────────────────────────────────────────

export const selectFoldersKpis = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.foldersKPIs
);

// ─── Drafts ─────────────────────────────────────────────────────────────────

export const selectDrafts = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.drafts
);

// ─── Mentions ────────────────────────────────────────────────────────────────

export const selectMentions = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.mentions
);

// ─── Sent Messages ────────────────────────────────────────────────────────────

export const selectSentMessages = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.sentMessages
);

// ─── Loading ─────────────────────────────────────────────────────────────────

export const selectLoading = createSelector(
  selectSmartInboxState,
  (state: SmartInboxState) => state.loading
);

export const selectGetLoading = createSelector(
  selectLoading,
  (loading) => loading.get
);

export const selectUpdateLoading = createSelector(
  selectLoading,
  (loading) => loading.update
);
